import React, { useState, useEffect } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import { Drawer, Table, Tag, Switch, Tooltip, Icon, message, Checkbox, Button, Modal } from 'antd';
import { SkeletonBlock } from '../styles';

import { request } from '~/components/Instance';
import { deliveryMethod } from '~/_domains/customers/drawer/data';
import { getWhatsappMessage } from '~/_domains/calendarTasks/helpers';
import ContentCopy from '~/helpers/contentCopy';

const { Column } = Table;

const NOTIFICATION_CHANNELS = [
  { key: 'panel_sent_at', label: 'Painel', color: 'purple' },
  { key: 'email_sent_at', label: 'Email', color: 'blue' },
  { key: 'whatsapp_sent_at', label: 'WhatsApp', color: 'green' },
  { key: 'protocol_generated_at', label: 'Protocolo', color: 'orange' },
];

const formatDateTime = (value) => moment(value).format('DD/MM/YYYY [às] HH:mm');

// Guias do registro: prioriza os arquivos marcados como guia de pagamento
const getGuias = (row) => {
  const allFiles = row.files || [];
  const guias = allFiles.filter((file) => file.requires_payment);
  return guias.length > 0 ? guias : allFiles;
};

// Confirmação de entrega da guia, por método de entrega do cliente
// (customers.doc_delivery_method — ver ~/_domains/customers/drawer/data).
//
// No Painel Web quem confirma é o próprio cliente, baixando a guia. Nos
// demais o cliente nunca passa pelo painel, então quem confirma é o que o
// escritório fez: o email disparado, o protocolo gerado.
const DELIVERY = {
  0: {
    column: 'email_sent_at',
    notice: 'Avisado por email',
    byCustomer: false,
    step: 'Email',
    done: 'Enviado',
    pending: 'Não Enviado',
    icon: 'mail',
    doneTip: 'Email enviado em',
    waitingTip: 'Aguardando envio por email',
    overdueTip: 'Vencido sem envio por email',
    lockedTip: 'Guia enviada por email em',
  },
  1: {
    column: 'protocol_generated_at',
    notice: 'Avisado por protocolo',
    byCustomer: false,
    step: 'Protocolo',
    done: 'Protocolado',
    pending: 'Não Protocolado',
    icon: 'file-done',
    doneTip: 'Protocolo gerado em',
    waitingTip: 'Aguardando protocolo do escritório',
    overdueTip: 'Vencido sem protocolo gerado',
    lockedTip: 'Guia protocolada em',
  },
  2: {
    column: 'viewed_at',
    notice: 'Cliente baixou a guia',
    byCustomer: true,
    step: 'Baixa',
    done: 'Baixado',
    pending: 'Não Baixado',
    icon: 'download',
    waitingTip: 'Aguardando baixa do cliente',
    overdueTip: 'Vencido sem baixa do cliente',
  },
  3: {
    column: 'whatsapp_sent_at',
    notice: 'Avisado por WhatsApp',
    byCustomer: false,
    step: 'WhatsApp',
    done: 'Enviado',
    pending: 'Não Enviado',
    icon: 'message',
    doneTip: 'WhatsApp enviado em',
    waitingTip: 'Aguardando envio por WhatsApp',
    overdueTip: 'Vencido sem envio por WhatsApp',
    lockedTip: 'Guia enviada por WhatsApp em',
  },
};

// Método não preenchido cai no Painel Web, que era o comportamento único
// antes desta régua existir.
const deliveryConfig = (row) => DELIVERY[row.customer.doc_delivery_method] || DELIVERY[2];

const getDeliveryAt = (row, file) => file[deliveryConfig(row).column];

// Mesmo critério do módulo de Documentos enviados
const getFileStatus = (row, file) => {
  const deliveredAt = getDeliveryAt(row, file);

  if (moment(file.due_at).isBefore(moment(), 'day') && !deliveredAt) {
    return 'Vencido';
  }
  if (deliveredAt) {
    return 'Baixado';
  }
  return 'Não Baixado';
};

// Registro com todas as guias já entregues — mesma régua do filtro rápido
// "Ocultar já entregues". Devolve a entrega mais recente, que alimenta o
// tooltip; null quando ainda há guia sem entrega confirmada.
const getEntrega = (row) => {
  const files = getGuias(row);
  if (files.length === 0) return null;
  if (!files.every((file) => getDeliveryAt(row, file))) return null;

  return files.reduce((latest, file) => {
    if (!latest) return file;
    return getDeliveryAt(row, file) > getDeliveryAt(row, latest) ? file : latest;
  }, null);
};

// Os values continuam sendo a régua interna; o texto é genérico porque a
// mesma coluna mostra baixa (painel/email/whatsapp) e protocolo.
const FILE_STATUS_FILTERS = [
  { text: 'Vencido', value: 'Vencido' },
  { text: 'Entregue', value: 'Baixado' },
  { text: 'Não entregue', value: 'Não Baixado' },
];

const getFileStatusTag = (row, file) => {
  const status = getFileStatus(row, file);
  const config = deliveryConfig(row);

  if (status === 'Vencido') return <Tag color="#ff0a02">Vencido</Tag>;
  if (status === 'Baixado') {
    return <Tag color="#03b14e"><Icon type={config.icon} /> {config.done}</Tag>;
  }
  return <Tag color="#0071c0">{config.pending}</Tag>;
};

// Cor do vencimento no título: vencido, em cima da hora, na semana, com folga
const getDueTag = (dueDate) => {
  const days = moment(dueDate, 'YYYY-MM-DD').diff(moment().startOf('day'), 'days');
  if (days < 0) return { color: '#f5222d', label: days === -1 ? 'venceu ontem' : `venceu há ${Math.abs(days)} dias` };
  if (days === 0) return { color: '#fa8c16', label: 'vence hoje' };
  if (days === 1) return { color: '#fa8c16', label: 'vence amanhã' };
  if (days <= 7) return { color: '#faad14', label: `faltam ${days} dias` };
  return { color: '#52c41a', label: `faltam ${days} dias` };
};

const QUICK_FILTERS_KEY = 'conferencia_drawer_filtros';

const loadQuickFilters = () => {
  try {
    return JSON.parse(localStorage.getItem(QUICK_FILTERS_KEY)) || {};
  } catch (e) {
    return {};
  }
};

const ConferenceList = ({ visible, task, date, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [hideNoGuia, setHideNoGuia] = useState(!!loadQuickFilters().hideNoGuia);
  const [hideBaixados, setHideBaixados] = useState(!!loadQuickFilters().hideBaixados);
  const [savingCustomerId, setSavingCustomerId] = useState(null);
  const [copyingCustomerId, setCopyingCustomerId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!visible || !task) return;

    setLoading(true);
    request('get', `/conference/task/${task.id}/list/${date.format('YYYY-MM')}`, {}, false)
      .then((response) => {
        const data = response.data && response.data.data;
        setRows((data && data.customers) || []);
      })
      .catch(() => {
        message.error('Não foi possível carregar os clientes desta tarefa!');
      })
      .finally(() => setLoading(false));
  }, [visible, task]);

  const toggleNotified = (row, checked) => {
    setSavingCustomerId(row.customer.id);

    request('post', '/conference/notify', {
      task_id: task.id,
      customer_id: row.customer.id,
      date: date.format('YYYY-MM'),
      notified: checked,
    }, false)
      .then((response) => {
        const data = response.data && response.data.data;
        if (!data) return;

        setRows((currentRows) => currentRows.map((currentRow) =>
          currentRow.customer.id !== row.customer.id ? currentRow : {
            ...currentRow,
            task_record_id: data.task_record_id,
            notified_at: data.notified_at,
            notified_by: data.notified_by,
            notified_by_name: data.notified_by_name,
            notified_in_time: data.notified_in_time,
          }
        ));
        setHasChanges(true);
      })
      .catch(() => {
        message.error('Não foi possível atualizar o aviso!');
      })
      .finally(() => setSavingCustomerId(null));
  };

  // Nada a avisar: guia já entregue, sem movimento ou sem documento no mês.
  // Mesma régua que trava o switch "Avisado".
  const isNotifyLocked = (row) => !!getEntrega(row) || row.is_without_movement || row.has_not_document;

  // "Copiar aviso": o mesmo texto do envio da guia por WhatsApp, com link de
  // acesso novo. Copiar não marca envio; o "Avisado" segue manual, e o modal
  // oferece marcar na hora.
  const copyAviso = (row) => {
    const files = getGuias(row);
    if (files.length === 0) return;

    setCopyingCustomerId(row.customer.id);

    request('post', '/conference/aviso', { file_ids: files.map((file) => file.id) }, false)
      .then((response) => {
        const data = response.data && response.data.data;
        const avisoFiles = (data && data.files) || [];
        if (avisoFiles.length === 0) {
          message.error('Não foi possível montar o aviso!');
          return;
        }

        const text = getWhatsappMessage(row.customer.name, avisoFiles);
        const plainText = text.replace(/%0a/g, '\n');

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(plainText)
            .then(() => message.success('Aviso copiado.'))
            .catch(() => { /* sem permissão: o texto fica no modal para copiar */ });
        }

        const canMarkNotified = !row.notified_at && !isNotifyLocked(row);

        Modal.info({
          title: 'Aviso para o cliente',
          content: <ContentCopy content={text} />,
          width: 560,
          icon: null,
          maskClosable: true,
          closable: true,
          okCancel: canMarkNotified,
          okText: canMarkNotified ? 'Marcar como avisado' : 'Fechar',
          cancelText: 'Fechar',
          onOk: () => {
            if (canMarkNotified) toggleNotified(row, true);
          },
        });
      })
      .catch(() => {
        message.error('Não foi possível montar o aviso!');
      })
      .finally(() => setCopyingCustomerId(null));
  };

  // Mesma régua do card da tarefa (ConferenceController::tasks): o cliente
  // está avisado quando alguém ligou o "Avisado" ou quando a guia já chegou
  // nele pelo método de entrega (email enviado, protocolo gerado, guia
  // baixada). Vale o que aconteceu primeiro; sem movimento e sem documento
  // não têm guia e contam como resolvidos.
  const renderPrazoTag = (row) => {
    if (row.is_without_movement) return <Tag color="green">Sem movimento</Tag>;
    if (row.has_not_document) return <Tag color="green">Sem documento</Tag>;

    const entrega = getEntrega(row);
    const deliveredAt = entrega ? getDeliveryAt(row, entrega) : null;
    const automatic = !!deliveredAt && (!row.notified_at || moment(deliveredAt).isBefore(moment(row.notified_at)));

    // Cada etiqueta diz se o cliente foi avisado e como; a cor diz se foi
    // no prazo (verde) ou depois do vencimento (vermelho). Data no tooltip.
    const prazoTip = (text, inTime) => inTime
      ? `${text} — no prazo`
      : `${text} — fora do prazo (venceu ${moment(row.due_date, 'YYYY-MM-DD').format('DD/MM')})`;

    if (automatic) {
      const config = deliveryConfig(row);
      const inTime = moment(deliveredAt).format('YYYY-MM-DD') <= row.due_date;
      const when = config.byCustomer
        ? `Cliente baixou a guia em ${formatDateTime(deliveredAt)}`
        : `${config.doneTip} ${formatDateTime(deliveredAt)}`;

      return (
        <Tooltip title={prazoTip(when, inTime)}>
          <Tag color={inTime ? 'green' : 'red'}>{config.notice}</Tag>
        </Tooltip>
      );
    }

    if (row.notified_at) {
      const when = `Marcado como avisado${row.notified_by_name ? ` por ${row.notified_by_name}` : ''} em ${formatDateTime(row.notified_at)}`;
      return (
        <Tooltip title={prazoTip(when, row.notified_in_time)}>
          {row.notified_in_time
            ? <Tag color="green">Avisado no prazo</Tag>
            : <Tag color="red">Avisado fora do prazo</Tag>}
        </Tooltip>
      );
    }

    const isOverdue = moment().startOf('day').isAfter(moment(row.due_date, 'YYYY-MM-DD'));
    return isOverdue
      ? <Tag color="red">Não avisado</Tag>
      : <Tag color="gold">Aguardando aviso</Tag>;
  };

  // Linha do tempo da guia: Envio (escritório) → Aviso (canais) → Baixa
  // (administração). Todo o detalhe fica no hover de cada passo.
  const renderTimeline = (row) => {
    const files = getGuias(row);
    if (files.length === 0) {
      return <span style={{ color: '#bfbfbf', fontSize: 12 }}>Nenhuma guia enviada</span>;
    }

    return files.map((file) => {
      const sentChannels = NOTIFICATION_CHANNELS.filter((channel) => file[channel.key]);
      const isOverdue = getFileStatus(row, file) === 'Vencido';
      const deliveredAt = getDeliveryAt(row, file);
      const config = deliveryConfig(row);

      const steps = [
        {
          label: 'Envio',
          done: !!file.created_at,
          tip: file.created_by_name && file.created_at
            ? `${file.type || file.description} — enviado por ${file.created_by_name} em ${formatDateTime(file.created_at)}`
            : (file.type || file.description || 'Guia enviada'),
        },
        {
          label: 'Aviso',
          done: sentChannels.length > 0,
          tip: sentChannels.length > 0
            ? sentChannels.map((channel) => `${channel.label} em ${formatDateTime(file[channel.key])}`).join(' · ')
            : 'Nenhum canal disparado ainda',
        },
        {
          label: config.step,
          done: !!deliveredAt,
          alert: isOverdue,
          tip: deliveredAt
            ? (config.byCustomer
              ? `Baixado por ${file.viewed_by_name || 'cliente'} em ${formatDateTime(deliveredAt)}`
              : `${config.doneTip} ${formatDateTime(deliveredAt)}`)
            : (isOverdue ? config.overdueTip : config.waitingTip),
        },
      ];

      return (
        <div key={file.id} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 6 }}>
          {steps.map((step, index) => {
            const color = step.alert && !step.done ? '#f5222d' : step.done ? '#52c41a' : '#d9d9d9';
            return (
              <React.Fragment key={step.label}>
                {index > 0 && (
                  <div style={{
                    width: 26, height: 2, marginTop: 8,
                    background: step.done ? '#95de64' : '#e8e8e8',
                  }} />
                )}
                <Tooltip title={step.tip}>
                  <div style={{ textAlign: 'center', cursor: 'help', width: 42 }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: '50%', margin: '0 auto',
                      background: step.done ? '#52c41a' : '#fff',
                      border: '2px solid ' + color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {step.done && <Icon type="check" style={{ fontSize: 9, color: '#fff' }} />}
                      {!step.done && step.alert && <Icon type="close" style={{ fontSize: 9, color: '#f5222d' }} />}
                    </div>
                    <div style={{
                      fontSize: 10, marginTop: 2,
                      color: step.done ? '#389e0d' : step.alert ? '#cf1322' : '#8c8c8c',
                    }}>
                      {step.label}
                    </div>
                  </div>
                </Tooltip>
              </React.Fragment>
            );
          })}
        </div>
      );
    });
  };

  const renderFileStatus = (row) => {
    const files = getGuias(row);
    if (files.length === 0) return null;

    return files.map((file) => (
      <div key={file.id} style={{ marginBottom: 4 }}>
        {getFileStatusTag(row, file)}
      </div>
    ));
  };

  useEffect(() => {
    try {
      localStorage.setItem(QUICK_FILTERS_KEY, JSON.stringify({ hideNoGuia, hideBaixados }));
    } catch (e) { /* armazenamento indisponível não é crítico */ }
  }, [hideNoGuia, hideBaixados]);

  // Quick filters: esconder clientes sem guia enviada e/ou com tudo já baixado
  const visibleRows = rows.filter((row) => {
    const files = getGuias(row);
    if (hideNoGuia && files.length === 0) return false;
    if (hideBaixados && getEntrega(row)) return false;
    return true;
  });

  const renderDeliveryMethod = (row) => {
    const method = deliveryMethod.find((option) => option.value === row.customer.doc_delivery_method);
    if (!method) return null;
    return <Tag color={method.color}>{method.text}</Tag>;
  };

  return (
    <Drawer
      title={
        <span>
          Conferência — {task ? task.name : ''}
          {task && (
            <Tag color={getDueTag(task.due_date).color} style={{ marginLeft: 12 }}>
              Vencimento {moment(task.due_date).format('DD/MM/YYYY')} · {getDueTag(task.due_date).label}
            </Tag>
          )}
        </span>
      }
      width={1400}
      placement="right"
      visible={visible}
      onClose={() => onClose(hasChanges)}
      destroyOnClose={true}
      style={{ height: '100%', overflow: 'auto', paddingBottom: 53 }}
    >
      {loading ? (
        <div>
          <SkeletonBlock height={38} style={{ marginBottom: 4 }} />
          {Array.from({ length: 10 }).map((_, index) => (
            <div key={index} style={{ display: 'flex', gap: 16, padding: '10px 0', alignItems: 'center' }}>
              <SkeletonBlock width="26%" height={14} />
              <SkeletonBlock width="14%" height={14} />
              <SkeletonBlock width="12%" height={14} />
              <SkeletonBlock width="16%" height={14} />
              <SkeletonBlock width="10%" height={22} round />
              <SkeletonBlock width="12%" height={14} />
            </div>
          ))}
        </div>
      ) : (
        <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Filtros rápidos
          </span>
          <Checkbox checked={hideNoGuia} onChange={(e) => setHideNoGuia(e.target.checked)}>
            Ocultar sem guia enviada
          </Checkbox>
          <Checkbox checked={hideBaixados} onChange={(e) => setHideBaixados(e.target.checked)}>
            Ocultar já entregues
          </Checkbox>
          {(hideNoGuia || hideBaixados) && visibleRows.length !== rows.length && (
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>
              mostrando {visibleRows.length} de {rows.length} — {rows.length - visibleRows.length} oculto(s)
            </span>
          )}
        </div>
        <Table
          rowKey={(row) => row.customer.id}
          size="small"
          dataSource={visibleRows}
          pagination={false}
          scroll={{ x: 1250 }}
        >
          <Column
            title="Executado"
            key="executed"
            width={85}
            align="center"
            render={(_, row) => (
              <Tooltip title={row.executed_at ? `Executado em ${formatDateTime(row.executed_at)}` : 'Não executado'}>
                <Switch checked={!!row.executed_at} disabled size="small" />
              </Tooltip>
            )}
          />
          <Column
            title="Revisado"
            key="revised"
            width={85}
            align="center"
            render={(_, row) => (
              <Tooltip title={row.revised_at ? `Revisado em ${formatDateTime(row.revised_at)}` : 'Não revisado'}>
                <Switch checked={!!row.revised_at} disabled size="small" />
              </Tooltip>
            )}
          />
          <Column
            title="S. de entrega"
            key="delivery"
            width={110}
            render={(_, row) => renderDeliveryMethod(row)}
          />
          <Column
            title="Cod"
            key="cod"
            width={70}
            render={(_, row) => row.customer.cod}
          />
          <Column
            title="Cliente"
            key="customer"
            width={280}
            ellipsis={true}
            render={(_, row) => row.customer.name}
          />
          <Column
            title="Linha do tempo"
            key="timeline"
            width={230}
            render={(_, row) => renderTimeline(row)}
          />
          <Column
            title="Status"
            key="file_status"
            width={120}
            filters={FILE_STATUS_FILTERS}
            onFilter={(value, row) => getGuias(row).some((file) => getFileStatus(row, file) === value)}
            render={(_, row) => renderFileStatus(row)}
          />
          <Column
            title="Aviso"
            key="aviso"
            width={130}
            align="center"
            render={(_, row) => {
              const hasGuia = getGuias(row).length > 0;
              const button = (
                <Button
                  size="small"
                  icon="copy"
                  disabled={!hasGuia}
                  loading={copyingCustomerId === row.customer.id}
                  onClick={() => copyAviso(row)}
                >
                  Copiar aviso
                </Button>
              );

              if (hasGuia) return button;

              // Botão desabilitado não dispara hover: o span recebe o Tooltip.
              return (
                <Tooltip title="Nenhuma guia enviada para este cliente">
                  <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>{button}</span>
                </Tooltip>
              );
            }}
          />
          <Column
            title="Avisado"
            key="notified"
            width={90}
            align="center"
            render={(_, row) => {
              // Nada a avisar = objetivo cumprido: liga e trava o switch, para
              // não desfazer nem remarcar o que já se resolveu. Vale para guia
              // entregue (baixa, email, whatsapp ou protocolo, conforme o
              // método) e para quem não tem guia no mês.
              const entrega = getEntrega(row);
              const isBlocked = row.is_without_movement || row.has_not_document;
              const locked = !!entrega || isBlocked;

              const switchNode = (
                <Switch
                  checked={locked || !!row.notified_at}
                  disabled={locked}
                  loading={savingCustomerId === row.customer.id}
                  onChange={(checked) => toggleNotified(row, checked)}
                />
              );

              let tip = null;
              if (entrega) {
                const config = deliveryConfig(row);
                const entregueEm = formatDateTime(getDeliveryAt(row, entrega));

                tip = config.byCustomer
                  ? `Cliente já baixou a guia${entrega.viewed_by_name ? ` (${entrega.viewed_by_name})` : ''} em ${entregueEm} — não é mais necessário avisar`
                  : `${config.lockedTip} ${entregueEm} — não é mais necessário avisar`;
              } else if (isBlocked) {
                tip = row.is_without_movement
                  ? 'Cliente sem movimento no mês — não há guia a avisar'
                  : 'Cliente sem documento no mês — não há guia a avisar';
              } else if (row.notified_at) {
                tip = `Avisado${row.notified_by_name ? ` por ${row.notified_by_name}` : ''} em ${formatDateTime(row.notified_at)}`;
              }

              if (!tip) return switchNode;

              // Switch desabilitado vira <button disabled>, que não dispara
              // hover — é o span que recebe o evento do Tooltip.
              return (
                <Tooltip title={tip}>
                  <span style={{ display: 'inline-block', cursor: locked ? 'not-allowed' : 'pointer' }}>
                    {switchNode}
                  </span>
                </Tooltip>
              );
            }}
          />
          <Column
            title="Prazo"
            key="prazo"
            width={175}
            render={(_, row) => renderPrazoTag(row)}
          />
        </Table>
        </>
      )}
    </Drawer>
  );
};

export default ConferenceList;
